import React from "react";
import { Link } from "react-router-dom";
import { SiTelegram, SiX } from "react-icons/si";
import { FiBarChart2 } from "react-icons/fi";
import { Rocket, Sparkles, ArrowRight } from "lucide-react";
import type { IconType, IconBaseProps } from "react-icons";

type CommunityLink = {
  label: string;
  href: string;
  icon: IconType;
};

const HeroSection: React.FC = () => {
  const communityLinks: CommunityLink[] = [
    { label: "Join Telegram", href: "https://t.me/aladdinlaunchpad", icon: SiTelegram },
    { label: "Follow on X", href: "https://x.com/aladdinlaunch", icon: SiX },
    { label: "View Chart", href: "https://dexscreener.com/solana/ALDNwmYLE8dK19PPgmWA4gSpFzvRozsG8y2J48SL6Jpo", icon: FiBarChart2 },
  ];

  const scrollToAbout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative py-4 px-4 overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-20 w-72 h-72 bg-purple-600/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-600/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          
          {/* Main heading */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent">
              Launch Your Token
            </span>
            <br />
            <span className="text-white">with Genie Magic</span>
          </h1>
          
          {/* Description */}
          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            Solana's most powerful token launchpad with advanced bundling, 
            sniping tools, and transparent RevShare economics
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/create-token"
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl font-semibold text-lg hover:opacity-90 transition-all transform hover:scale-105 flex items-center justify-center space-x-2"
            >
              <Rocket className="w-5 h-5" />
              <span>Launch Token</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#about"
              onClick={scrollToAbout}
              className="px-8 py-4 bg-gray-800/50 border border-gray-700 hover:border-purple-500 rounded-xl font-semibold text-lg transition-all flex items-center justify-center space-x-2"
            >
              <span>Learn More</span>
            </a>
          </div>

          {/* Community Links */}
          <div className="max-w-2xl mx-auto">
            <p className="text-sm text-gray-500 mb-6">Join our community</p>
            
            {/* Mobile: Stack vertically, Desktop: Horizontal layout */}
            <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-6">
              {communityLinks.map((link, index) => {
                const IconComp = link.icon as React.ComponentType<IconBaseProps>;
                return (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center sm:justify-start space-x-3 px-6 py-3 bg-gray-800/50 border border-gray-700 hover:border-purple-400 rounded-lg transition-all w-full sm:w-auto min-h-[48px]"
                  >
                    <IconComp size={20} className="text-purple-300 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </a>
                );
              })}
            </div>
            
            {/* Alternative: Grid layout for mobile (uncomment to use) */}
            {/* 
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-lg sm:max-w-none mx-auto">
              {communityLinks.map((link, index) => {
                const IconComp = link.icon as React.ComponentType<IconBaseProps>;
                return (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-center space-x-2 px-4 py-3 bg-gray-800/50 border border-gray-700 hover:border-purple-400 rounded-lg transition-all min-h-[48px]"
                  >
                    <IconComp size={18} className="text-purple-300 group-hover:text-purple-400 transition-colors flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{link.label}</span>
                  </a>
                );
              })}
            </div>
            */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;